import { useState, useEffect } from 'react';
import { ArticleCard } from '@/components/ArticleCard';
import { Header } from '@/components/Header';
import { Sidebar } from '@/components/Sidebar';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/news-supabase-integration/supabase/client';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';
import { cn } from '@/lib/utils';
import GlobalAudioBar from '@/components/GlobalAudioBar';

const BookmarkedArticles = () => {
  const navigate = useNavigate();
  const [bookmarkedArticles, setBookmarkedArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const { toast } = useToast();
  const isDarkMode = useIsDarkMode();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const loadBookmarkedArticles = () => {
      const articles = JSON.parse(localStorage.getItem('bookmarkedArticles') || '{}');
      setBookmarkedArticles(Object.values(articles).sort((a: any, b: any) =>
        new Date(b.bookmarkedAt).getTime() - new Date(a.bookmarkedAt).getTime()
      ));
      setLoading(false);
    };

    loadBookmarkedArticles();
    window.addEventListener('storage', loadBookmarkedArticles);

    return () => {
      window.removeEventListener('storage', loadBookmarkedArticles);
    };
  }, []);

  const [searchQuery, setSearchQuery] = useState('');

  const headerProps = {
    searchQuery,
    onSearchChange: setSearchQuery,
    onFilterClick: () => { },
    onRefresh: () => {
      setLoading(true);
      loadBookmarkedArticles();
    },
    onMenuClick: () => setIsSidebarOpen(true),
  };

  const loadBookmarkedArticles = () => {
    const articles = JSON.parse(localStorage.getItem('bookmarkedArticles') || '{}');
    const articleArray = Object.values(articles)
      .sort((a: any, b: any) => new Date(b.bookmarkedAt).getTime() - new Date(a.bookmarkedAt).getTime())
      .filter((article: any) =>
        searchQuery ?
          article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          article.description.toLowerCase().includes(searchQuery.toLowerCase())
          : true
      );
    setBookmarkedArticles(articleArray);
    setLoading(false);
  };

  const handleSummarize = async (article: any) => {
    if (!article.id) {
      toast({
        title: "Error",
        description: "Article ID is required for summarization",
        variant: "destructive",
      });
      return;
    }

    setLoadingStates(prev => ({ ...prev, [article.id]: true }));

    try {
      const response = await supabase.functions.invoke('summarize-article', {
        body: {
          articleId: article.id,
          title: article.title,
          description: article.description,
          source: article.source?.name,
          publishedAt: article.publishedAt,
        },
      });

      if (response.error) {
        throw response.error;
      }

      if (response.data?.success) {
        // Update article in localStorage with the new summary
        const articles = JSON.parse(localStorage.getItem('bookmarkedArticles') || '{}');
        articles[article.id] = {
          ...articles[article.id],
          aiSummary: response.data.summary
        };
        localStorage.setItem('bookmarkedArticles', JSON.stringify(articles));

        // Update UI
        setBookmarkedArticles(prev =>
          prev.map(a => a.id === article.id ? { ...a, aiSummary: response.data.summary } : a)
        );
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate summary",
        variant: "destructive",
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [article.id]: false }));
    }
  };

  const pageBgClass = cn(
    "min-h-screen transition-colors duration-300",
    isDarkMode ? "bg-[#303030] text-slate-100" : "bg-[#f3f4f8] text-slate-900"
  );

  if (loading) {
    return (
      <div className={pageBgClass}>
        <Header {...headerProps} />
        <div className="container mx-auto px-4 py-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className={pageBgClass}>
      <Sidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen((o) => !o)}
        onNewChat={() => navigate("/")}
        isDarkMode={isDarkMode}
        onAuth={() => { }}
        user={null}
      />

      <Header {...headerProps} />

      <main className="container mx-auto px-1 xs:px-6 py-10 lg:py-12">
        <div
          className={cn(
            "rounded-4xl border-0 px-0 xs:px-8 py-6 lg:px-12 lg:py-10 transition-colors")}
        >
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/ai-news')}
              className={cn(
                "h-10 w-10 rounded-xl border",
                isDarkMode
                  ? "border-white/10 text-gray-200 hover:bg-white/10"
                  : "border-slate-200 text-slate-700 hover:bg-slate-100"
              )}
              title="Back to AI News"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Bookmarked Articles</h1>
            </div>
          </div>

          {bookmarkedArticles.length === 0 ? (
            <div
              className={cn(
                "flex flex-col items-center justify-center rounded-3xl border px-6 py-16 text-center",
                isDarkMode
                  ? "border-white/5 bg-white/5 text-slate-300"
                  : "border-slate-200 bg-slate-50/70 text-slate-600"
              )}
            >
              <span className="mb-3 text-lg font-semibold">No bookmarked articles yet</span>
              <p className="max-w-sm text-sm text-muted-foreground">
                Tap the bookmark icon on any article to save it and it will show up here instantly.
              </p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
              {bookmarkedArticles.map((article: any, index: number) => (
                <ArticleCard
                  key={`${article.url}-${index}`}
                  article={article}
                  onSummarize={handleSummarize}
                  isDarkMode={isDarkMode}
                  isGeneratingSummary={loadingStates[article.id || '']}
                  prefetchUrls={bookmarkedArticles
                    .slice(index + 1, index + 3)
                    .map((a: any) => a.imageUrl)
                    .filter((u: any): u is string => Boolean(u))}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      {/* Sticky bottom global audio bar */}
      <GlobalAudioBar isDarkMode={isDarkMode} />
    </div>
  );
}

export default BookmarkedArticles;
